import { Component } from '@angular/core'
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms'
import { BehaviorSubject } from 'rxjs'
import { AuthService } from '../auth.service'
import { AsyncPipe } from '@angular/common'
import { MatButton } from '@angular/material/button'
import { RouterLink } from '@angular/router'
import { MatFormField, MatLabel, MatInput, MatPrefix } from '@angular/material/input'
import { MatIcon } from '@angular/material/icon'

@Component({
    selector: 'app-password-reset',
    template: `
        <div>
            <h1 class="text-2xl font-bold text-gray-900 mb-1">Reset password</h1>
            <p class="text-sm text-gray-500 mb-8">Enter your email and we'll send you a reset link</p>

            @if (sent$ | async) {
                <div class="flex flex-col items-center gap-4 py-6">
                    <div class="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center">
                        <mat-icon class="!text-3xl text-success">check_circle</mat-icon>
                    </div>
                    <p class="text-sm text-gray-600 text-center">
                        A password reset link has been sent to your email address.
                    </p>
                    <a routerLink="/login" mat-flat-button class="bg-btn-primary text-btn-primary-text">
                        Back to Sign in
                    </a>
                </div>
            } @else {
                <form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col gap-1">
                    <mat-form-field class="w-full" appearance="outline" subscriptSizing="dynamic">
                        <mat-label>Email</mat-label>
                        <mat-icon matPrefix class="mr-2 text-gray-400">email</mat-icon>
                        <input matInput formControlName="email" type="email" autocomplete="email" />
                    </mat-form-field>

                    @if (error$ | async; as error) {
                        <div class="flex items-center gap-2 text-danger text-sm mb-2 p-3 bg-red-50 rounded-lg">
                            <mat-icon class="!text-base !w-4 !h-4">error_outline</mat-icon>
                            {{ error }}
                        </div>
                    }

                    <button
                        mat-flat-button
                        type="submit"
                        class="!h-12 !rounded-lg w-full bg-btn-primary text-btn-primary-text"
                        [disabled]="loading$ | async">
                        Send Reset Link
                    </button>
                </form>
                <div class="mt-6 text-center">
                    <a routerLink="/login" class="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                        Back to Sign in
                    </a>
                </div>
            }
        </div>
    `,
    imports: [
        MatButton,
        RouterLink,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatPrefix,
        MatIcon,
        AsyncPipe,
    ],
})
export class PasswordResetComponent {
    form = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
    })

    loading$ = new BehaviorSubject(false)
    error$ = new BehaviorSubject<string | null>(null)
    sent$ = new BehaviorSubject(false)

    constructor(private auth: AuthService) {}

    onSubmit() {
        if (this.form.invalid) return

        this.loading$.next(true)
        this.error$.next(null)

        this.auth.resetPassword(this.form.value.email!).subscribe({
            next: () => {
                this.loading$.next(false)
                this.sent$.next(true)
            },
            error: (err) => {
                this.loading$.next(false)
                this.error$.next(err?.message || 'Failed to send reset link.')
            },
        })
    }
}
